import { Grid } from '@material-ui/core';
import { OnboardingFulfilledBy } from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ApplicationStepContainer } from '@/components/modules/Application/components/_shared/components/ApplicationStepContainer';
import { ApplicationStepNav } from '@/components/modules/Application/components/_shared/components/ApplicationStepNav';
import { Section } from '@/components/modules/Application/components/_shared/components/Section/Section';
import { SectionTitle } from '@/components/modules/Application/components/_shared/components/Section/SectionTitle';
import { MERCHANT_POS_ALLOWED_PAYMENT_METHOD_TYPES } from '@/components/modules/Application/components/_shared/constants/MerchantPosAllowedPaymentMethodTypes';
import { CheckoutSessionCreationCard } from '@/components/modules/Application/components/CheckoutSessionCreation/components/CheckoutSessionCreationCard';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { useCreateCheckoutSession } from '@/components/modules/Application/hooks/useCreateCheckoutSession';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

type Destination = 'merchantPos' | 'sendToClient';

export const CheckoutSessionCreation = () => {
  const {
    state: { selectedAgent, paymentMethodEntities, customerData },
  } = useApplicationContext();
  const { createCheckoutSession } = useCreateCheckoutSession();
  const { onPrev, setCompleted } = useApplicationNavigation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<Destination | null>(null);

  const onSubmit = async (destination: Destination) => {
    setLoading(destination);
    try {
      const checkoutSession = await createCheckoutSession(customerData);
      if (!checkoutSession) {
        throw new Error('Checkout session not created');
      }
      setCompleted();
      setLoading(null);
      navigate(
        `${generatePath(DYNAMIC_PAGE_PATHS.CHECKOUT_SESSION_DETAIL, {
          checkoutSessionId: checkoutSession.checkoutSessionId,
        })}${destination === 'sendToClient' ? '?share=true' : ''}`,
      );
    } catch (err) {
      console.error(err);
    }
  };

  if (!selectedAgent) {
    return null;
  }

  const allowedMerchantPosOnboardingFulfilledBy = selectedAgent.allowedOnboardingFulfilledByTypes.includes(
    OnboardingFulfilledBy.MERCHANT_POS,
  );

  const allowedSentToCustomerOnboardingFulfilledBy = selectedAgent.allowedOnboardingFulfilledByTypes.some((type) => {
    return [
      OnboardingFulfilledBy.BORROWER_POS,
      OnboardingFulfilledBy.PAY_BY_LINK,
      OnboardingFulfilledBy.BORROWER_ONLINE,
    ].includes(type);
  });

  let isPaymentMethodAllowedToMerchantPos = selectedAgent.allowedPaymentMethodTypes.some((pm) =>
    MERCHANT_POS_ALLOWED_PAYMENT_METHOD_TYPES.includes(pm),
  );
  if (paymentMethodEntities && paymentMethodEntities.length > 0) {
    isPaymentMethodAllowedToMerchantPos = paymentMethodEntities.some(
      (pm) => MERCHANT_POS_ALLOWED_PAYMENT_METHOD_TYPES.includes(pm.paymentMethodType) && pm.isSelected,
    );
  }

  const isAllowedMerchantPos = allowedMerchantPosOnboardingFulfilledBy && isPaymentMethodAllowedToMerchantPos;
  const isAllowedSentToCustomer = allowedSentToCustomerOnboardingFulfilledBy;

  return (
    <ApplicationStepContainer>
      <Section>
        <SectionTitle title={'CHECKOUT_SESSION_CREATION'} subtitle={'CHECKOUT_SESSION_CREATION_DESCRIPTION'} />
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <CheckoutSessionCreationCard
              titleIconName={'storefront'}
              titleTextId={'START_MERCHANT_POS_PROCESS'}
              descriptionTextId={
                !allowedMerchantPosOnboardingFulfilledBy
                  ? 'MERCHANT_POS_PROCESS_NOT_ALLOWED_BY_AGENT_DESCRIPTION'
                  : !isPaymentMethodAllowedToMerchantPos
                  ? 'MERCHANT_POS_PROCESS_NOT_ALLOWED_BY_PAYMENT_METHODS_DESCRIPTION'
                  : 'MERCHANT_POS_PROCESS_DESCRIPTION'
              }
              buttonIconName={'arrow_forward'}
              buttonLabelTextId={'START_NOW'}
              onClick={() => onSubmit('merchantPos')}
              disabled={!isAllowedMerchantPos || !!loading}
              loading={loading === 'merchantPos'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CheckoutSessionCreationCard
              titleIconName={'send'}
              titleTextId={'SEND_TO_MY_CLIENT'}
              descriptionTextId={'SHARE_PAYMENT_LINK_WITH_YOUR_CLIENT'}
              buttonLabelTextId={'SEND'}
              buttonIconName={'send'}
              onClick={() => onSubmit('sendToClient')}
              disabled={!isAllowedSentToCustomer || !!loading}
              loading={loading === 'sendToClient'}
            />
          </Grid>
        </Grid>
      </Section>
      <ApplicationStepNav onPrev={onPrev} />
    </ApplicationStepContainer>
  );
};
